import { DeleteOutlined, EditOutlined, EyeFilled } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Tag, Tooltip } from 'antd';
import moment from 'moment';
import { PencilSimpleLine, Trash } from 'phosphor-react';
import React, { useRef } from 'react';
import videoPlaceholder from '../../../assets/images/imagePlaceholder.png';
import {
  ASSET_CATEGORY,
  DOMAIN_STATUS,
  MODULES,
  ROUTES,
  STATUS_COLORS,
  WORKSPACE_ROLE_LEVEL,
  WORKSPACE_ROLE_PERMISSION
} from '../../../common/constants';
import { status } from '../../../common/utils';
import { modalContext } from '../../../components/AppComponentContainer';
import FilterBar, { useFilterBar } from '../../../components/FilterBar';
import PageHeader from '../../../components/PageHeader';
import PageList from '../../../components/PageList';
import useCheckPermission from '../../../hooks/useCheckPermission';
import CustomCard from '../../component/card/Cscard';
import { DELETE_ASSET } from '../graphql/Mutations';
import { GET_ASSET_URL, GET_AUDIO_VIDEOS } from '../graphql/Queries';

const variablesSelector = ({
  limit,
  offset,
  search,
  sortField,
  sortOrder,
  isLive
}) => ({
  filter: {
    limit,
    skip: offset,
    search,
    categoryKey: ASSET_CATEGORY?.VIDEO,
    isLive
  },
  sort: {
    sortOn: sortField,
    sortBy: sortOrder
  }
});

const dataSelector = ({ assets }) => ({
  data: assets?.assets || [],
  count: assets?.count || 0
});

const columns = ({
  isPlatformAllowed,
  isDeleteAllowed,
  handlePreview,
  handleEdit,
  handleDelete,
  isViewOnly
}) => [
  {
    title: 'Video',
    dataIndex: 'serviceImageThumbnail',
    key: 'serviceImageThumbnail',
    editable: true,
    width: '12%',
    render: (text, record) => (
      <img
        onClick={() => handlePreview(record)}
        className="group-img-table pointer"
        src={
          record?.serviceVideoThumbnail ??
          record?.serviceImageThumbnail ??
          videoPlaceholder
        }
        alt={record?.title ?? ''}
      />
    )
  },
  {
    title: 'Title',
    dataIndex: 'title',
    key: 'title',
    width: '22%'
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    width: '22%'
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: '22%',
    render: (value, record) => (
      <Tag
        className="m-0 custom-tag"
        color={STATUS_COLORS[value]}
        title={value === DOMAIN_STATUS.ERRORED ? `${record?.reason}` : ''}
      >
        {status[value]}
      </Tag>
    )
  },
  ...(isPlatformAllowed || isDeleteAllowed
    ? [
        {
          title: 'Action',
          dataIndex: 'operation',
          width: '12%',
          render: (text, record) => (
            <div>
              {isPlatformAllowed && (
                <Tooltip title={`${isViewOnly ? 'View' : 'Edit'} Video`}>
                  {isViewOnly ? (
                    <EyeFilled
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(record?.id);
                      }}
                    />
                  ) : (
                    <EditOutlined
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(record?.id);
                      }}
                    />
                  )}
                </Tooltip>
              )}
              {isDeleteAllowed && (
                <span onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="Delete Video">
                    <DeleteOutlined onClick={() => handleDelete(record?.id)} />
                  </Tooltip>
                </span>
              )}
            </div>
          )
        }
      ]
    : [])
];

function Videos({ history }) {
  const ref = useRef();
  const filterProps = useFilterBar();
  const isAddEditAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.ASSET_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);
  const isDeleteAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.ASSET_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.DELETE]
    }
  ]);

  const isViewOnly = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.ASSET_MANAGEMENT,
      allowedPermissions: [WORKSPACE_ROLE_LEVEL.VIEW]
    }
  ]);

  const isPlatformAllowed = useCheckPermission([
    {
      moduleKey: WORKSPACE_ROLE_PERMISSION.PLATFORM_MANAGEMENT,
      allowedPermissions: [
        WORKSPACE_ROLE_LEVEL.VIEW,
        WORKSPACE_ROLE_LEVEL.EDIT,
        WORKSPACE_ROLE_LEVEL.DELETE
      ]
    }
  ]);

  const [deleteVideo] = useMutation(DELETE_ASSET, {
    onError() {}
  });

  const [fetchVideo] = useLazyQuery(GET_ASSET_URL, { onError: () => {} });

  const handleShowModal = () => {
    history?.push(`${ROUTES?.VIDEOS}/add`, { isUpdate: false });
  };

  const handleImportVideos = () => {
    history?.push(`${ROUTES?.VIDEOS}/import`);
  };

  const handleEdit = (id) => {
    history.push(`${ROUTES?.VIDEOS}/${id}/edit`, {
      isUpdate: true,
      videoId: id
    });
  };

  const handlePreview = (item) => {
    fetchVideo({
      variables: {
        where: {
          id: item?.id
        }
      }
    }).then(({ data }) => {
      if (ref.current.showPreview) {
        ref.current.showPreview({
          title: item.title,
          url: data?.assetAdmin?.url,
          type: ASSET_CATEGORY.VIDEO
        });
      }
    });
  };

  const handleDelete = async (id) => {
    modalContext?.confirm({
      title: 'Are you sure, you want to delete this video?',
      centered: true,
      okText: 'Yes',
      cancelText: 'No',
      okType: 'primary',
      async onOk() {
        deleteVideo({
          variables: { where: { id } }
        })
          .then(({ errors }) => {
            if (ref.current.removeItem && !errors) {
              ref.current.removeItem(id);
            }
          })
          .catch();
      }
    });
  };

  return (
    <>
      <PageHeader
        menu={MODULES?.ASSETS}
        handleShowModal={handleShowModal}
        handleImportVideos={handleImportVideos}
        isAddEditAllowed={isPlatformAllowed && isAddEditAllowed}
      />
      <FilterBar
        {...filterProps}
        SearchProps={{ placeholder: 'Search Videos' }}
      />
      <PageList
        ref={ref}
        filters={filterProps.filters}
        listMode={filterProps.listMode}
        query={GET_AUDIO_VIDEOS}
        variablesSelector={variablesSelector}
        dataSelector={dataSelector}
        limit={10}
        GridProps={{
          renderItem: (item) => (
            <CustomCard
              isLive={item?.isLive}
              onClick={() => {
                handlePreview(item);
              }}
              status={status[item.status]}
              languageCode={item.languageCode}
              statusColor={STATUS_COLORS[item.status]}
              alt={item?.title}
              src={
                item?.serviceVideoThumbnail ||
                item?.serviceImageThumbnail ||
                videoPlaceholder
              }
              title={moment(item?.createdAt).format('ll')}
              reason={item?.reason}
              heading={item?.title}
              description={item?.description}
              primaryOnClick={(e) => {
                e?.stopPropagation();
                e?.preventDefault();
                handleEdit(item?.id);
              }}
              secondaryOnClick={(e) => {
                e?.stopPropagation();
                e?.preventDefault();
                handleDelete(item?.id);
              }}
              primary={
                isPlatformAllowed &&
                (isViewOnly ? (
                  <>
                    <EyeFilled size={16} />
                    View
                  </>
                ) : (
                  <>
                    <PencilSimpleLine size={16} />
                    Edit
                  </>
                ))
              }
              secondary={
                isDeleteAllowed && (
                  <>
                    <Trash size={16} />
                    Delete
                  </>
                )
              }
            />
          )
        }}
        TableProps={{
          columns: (props) =>
            columns({
              ...props,
              isPlatformAllowed,
              isAddEditAllowed,
              isDeleteAllowed,
              handlePreview,
              handleEdit,
              handleDelete,
              isViewOnly
            })
        }}
      />
    </>
  );
}

export default Videos;
